import LegalPageLayout from "@/components/LegalPageLayout";
import SEO from "@/components/SEO";

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Privacy Policy"
      updated="13 July 2026"
    >
      <SEO
        title="Privacy Policy | Checkout DM"
        description="How Checkout DM collects, uses, and protects merchant and customer data across Instagram DM checkout, payments, and order fulfillment."
        path="/privacy-policy"
      />
      <p>
        This Privacy Policy explains how <strong>CheckoutDM</strong>{" "}
        ("CheckoutDM," "we," "us," or "our"), operated by M/s Healthy Celeb,
        collects, uses, stores, and protects information when businesses
        ("Merchants") and their customers ("Customers") use our service to
        facilitate product sales and order collection through Instagram
        Direct Messages.
      </p>

      <p>
        CheckoutDM integrates with the Meta Instagram Platform (Instagram
        Graph API) to allow Merchants to automate customer conversations and
        order-taking within Instagram DMs. By using CheckoutDM, you agree to
        the collection and use of information as described in this policy.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 From Merchants</h3>
      <ul>
        <li>Instagram Business Account ID and Access Token (used solely to send and receive messages on the Merchant's behalf)</li>
        <li>Business name, contact details, and product catalog information (product names, prices, descriptions, images)</li>
        <li>Payment configuration details (e.g. Razorpay API keys, UPI ID, or QR code images) supplied by the Merchant to receive payments</li>
        <li>Subscription and billing information related to the Merchant's CheckoutDM plan</li>
      </ul>

      <h3>1.2 From Customers (End Users messaging a Merchant's Instagram account)</h3>
      <ul>
        <li>Instagram-Scoped ID (IGSID) — a unique identifier Meta provides per conversation, not the Customer's public Instagram username or profile</li>
        <li>Information voluntarily provided during checkout: name, phone number, email address, and shipping address</li>
        <li>Order details: selected products, quantities, and order status</li>
        <li>
          Payment status information received from our payment processor
          (Razorpay) — we do <strong>not</strong> collect or store full card
          numbers, UPI PINs, or bank credentials; these are handled directly
          by Razorpay under its own security and compliance standards
        </li>
        <li>Message content necessary to process the order (e.g. product selection replies, address text)</li>
      </ul>

      <h3>1.3 Automatically Collected</h3>
      <ul>
        <li>Basic technical logs (timestamps, message delivery status, error logs) used for debugging and service reliability</li>
      </ul>

      <h2>2. How We Use Information</h2>
      <ul>
        <li>To operate the automated DM-to-checkout flow (product selection, detail collection, payment link generation)</li>
        <li>To process and fulfill orders between Customers and Merchants</li>
        <li>To generate and share payment links or payment instructions via Razorpay, UPI, or QR code</li>
        <li>To provide customer support to Merchants regarding their CheckoutDM account</li>
        <li>To maintain, secure, and improve the CheckoutDM service</li>
        <li>To comply with legal obligations, including tax and payment recordkeeping requirements</li>
      </ul>
      <p>
        We do <strong>not</strong> sell Customer or Merchant personal data to
        third parties. We do not use Customer conversation data for
        advertising purposes.
      </p>

      <h2>3. How Information Is Shared</h2>
      <ul>
        <li>
          <strong>With the relevant Merchant</strong> — a Customer's order and
          contact details are shared with the specific Merchant business the
          Customer is transacting with, so the Merchant can fulfill the order
        </li>
        <li>
          <strong>With Razorpay</strong> (our payment processor) — solely to
          generate payment links and confirm payment status
        </li>
        <li>
          <strong>With Meta Platforms, Inc.</strong> — as required to send and
          receive messages via the Instagram Graph API, subject to Meta's own
          Platform Terms and Data Policy
        </li>
        <li>
          <strong>With infrastructure providers</strong> — our systems run on
          Supabase (database hosting) and a self-managed server
          infrastructure, used solely to operate the service, not to
          independently use or sell the data
        </li>
        <li>We may disclose information if required by law, legal process, or to protect the rights, property, or safety of CheckoutDM, our Merchants, or the public</li>
      </ul>

      <h2>4. Data Storage and Security</h2>
      <p>
        Customer and Merchant data is stored in a Postgres database (hosted
        via Supabase, Singapore region) and processed through our automation
        infrastructure. We apply reasonable technical and organizational
        measures — including encrypted credential storage and access
        controls — to protect data against unauthorized access, alteration,
        or loss. However, no method of transmission or storage is 100%
        secure, and we cannot guarantee absolute security.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain Customer order data for as long as necessary to fulfill the
        order, resolve disputes, and comply with applicable tax, accounting,
        or legal obligations, after which it is deleted or anonymized.
        Merchants may request deletion of their account and associated data
        at any time, subject to legal retention requirements (e.g.
        transaction records required under Indian law).
      </p>

      <h2>6. Your Rights and Choices</h2>
      <ul>
        <li>
          <strong>Customers</strong> may contact the Merchant they transacted
          with, or contact us directly at{" "}
          <a href="mailto:support@checkoutdm.com">support@checkoutdm.com</a>,
          to request access to, correction of, or deletion of their personal
          data.
        </li>
        <li>
          <strong>Merchants</strong> may access, update, or delete their
          business data and disconnect their Instagram account from
          CheckoutDM at any time via their Merchant dashboard.
        </li>
        <li>
          To request deletion of your data, email{" "}
          <a href="mailto:support@checkoutdm.com">support@checkoutdm.com</a>{" "}
          with the subject line "Data Deletion Request" along with your
          Instagram handle or order details so we can locate your records.
          Your request will be processed within 30 days.
        </li>
      </ul>

      <h2>7. Children's Privacy</h2>
      <p>
        CheckoutDM is not directed at, and should not be used by, individuals
        under the age of 18 (or the applicable age of majority/consent in
        their jurisdiction). We do not knowingly collect personal data from
        children. If we become aware that we have inadvertently collected
        such data, we will delete it promptly.
      </p>

      <h2>8. Meta Platform Compliance</h2>
      <p>
        Our use of information received from the Instagram Graph API adheres
        to the{" "}
        <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noopener noreferrer">
          Meta Platform Terms
        </a>{" "}
        and{" "}
        <a href="https://www.facebook.com/about/privacy" target="_blank" rel="noopener noreferrer">
          Meta Data Policy
        </a>
        . We use Instagram data solely to provide the automated messaging and
        checkout functionality Merchants have configured, and not for any
        undisclosed purpose.
      </p>

      <h2>9. International Data Transfers</h2>
      <p>
        Our infrastructure may process and store data outside your country of
        residence, including in Singapore and India. By using CheckoutDM, you
        consent to this transfer and processing.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material
        changes will be reflected by updating the "Last updated" date above.
        Continued use of CheckoutDM after changes constitutes acceptance of
        the revised policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>For questions about this Privacy Policy or to exercise your data rights, contact us at:</p>
      <p>
        <a href="mailto:support@checkoutdm.com">support@checkoutdm.com</a>
        <br />
        #989, FF, Sector - 11, Panchkula, Haryana, India - 134112
      </p>
    </LegalPageLayout>
  );
}
